import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Database, FolderCog, FileText, Package, Users, HardHat,
  Wallet, CreditCard, LineChart, CalendarCheck, BarChart3, Archive, RefreshCw,
} from 'lucide-react';

const PHASES = [
  { value: 'phase-1', label: 'Phase 1 — Project Setup',         short: 'P1 Setup',        icon: FolderCog },
  { value: 'phase-2', label: 'Phase 2 — Client Billing',        short: 'P2 Billing',      icon: FileText },
  { value: 'phase-3', label: 'Phase 3 — Material Purchases',    short: 'P3 Materials',    icon: Package },
  { value: 'phase-4', label: 'Phase 4 — Payroll & Labor',       short: 'P4 Payroll',      icon: Users },
  { value: 'phase-5', label: 'Phase 5 — Subcontractors',        short: 'P5 Subs',         icon: HardHat },
  { value: 'phase-6', label: 'Phase 6 — Client Collections',    short: 'P6 Collections',  icon: Wallet },
  { value: 'phase-7', label: 'Phase 7 — Supplier Payments',     short: 'P7 Payments',     icon: CreditCard },
  { value: 'phase-8', label: 'Phase 8 — Project Monitoring',    short: 'P8 Monitoring',   icon: LineChart },
  { value: 'phase-9', label: 'Phase 9 — Month-End Accounting',  short: 'P9 Month-End',    icon: CalendarCheck },
  { value: 'phase-10', label: 'Phase 10 — Financial Statements', short: 'P10 Statements', icon: BarChart3 },
  { value: 'phase-11', label: 'Phase 11 — Project Closeout',    short: 'P11 Closeout',    icon: Archive },
];

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency', currency: 'PHP',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(amount || 0);
}

const STATUS_BADGE = {
  Active: 'bg-green-500/15 text-green-600 border-green-500/30',
  Completed: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
};

const CATEGORY_BADGE = {
  'Architectural / Design': 'bg-blue-500/15 text-blue-600 border-blue-500/30',
  'Construction': 'bg-green-500/15 text-green-600 border-green-500/30',
  'Change Order': 'bg-purple-500/15 text-purple-600 border-purple-500/30',
};

function P1SetupTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/accounting/p1-setup');
      setRows(res.data || []);
    } catch (e) {
      console.error('Failed to load P1 setup data:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <Card className="border-border/40" data-testid="p1-setup-table">
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <FolderCog className="w-4 h-4 text-primary" />
            Phase 1 — Project Setup
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Auto-extracted from Billing Monitoring & Project Directory — only items with a Project Contract Code.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="p1-refresh-btn"
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-border/60 hover:bg-muted/50 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-12 text-center text-sm text-muted-foreground animate-pulse">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            No contract-coded items yet. Add a contract amount in Billing Monitoring to generate codes.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[160px]">Project Contract Code</TableHead>
                  <TableHead className="w-[180px]">Project Name</TableHead>
                  <TableHead className="w-[150px]">Client</TableHead>
                  <TableHead className="w-[140px] text-right">Contract Amount</TableHead>
                  <TableHead className="w-[120px]">Start Date</TableHead>
                  <TableHead className="w-[140px]">Target Completion</TableHead>
                  <TableHead className="w-[140px] text-right">Approved Budget</TableHead>
                  <TableHead className="w-[110px]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={`${r.contract_code}-${i}`} data-testid={`p1-row-${r.contract_code}`}>
                    <TableCell>
                      <div className="font-mono font-medium text-sm">{r.contract_code}</div>
                      <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded border ${CATEGORY_BADGE[r.category] || ''}`}>
                        {r.category}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{r.project_name || '—'}</TableCell>
                    <TableCell className="text-sm">{r.client || '—'}</TableCell>
                    <TableCell className="font-mono text-right text-sm">{formatCurrency(r.contract_amount)}</TableCell>
                    <TableCell className="text-sm">{r.start_date || '—'}</TableCell>
                    <TableCell className="text-sm">{r.target_completion || '—'}</TableCell>
                    <TableCell className="font-mono text-right text-sm">{formatCurrency(r.approved_budget)}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-1 rounded border ${STATUS_BADGE[r.status] || ''}`}>
                        {r.status || 'Active'}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AccountingDatabaseTab() {
  const [active, setActive] = useState('phase-1');

  return (
    <Card className="glass-effect border-border/40">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Database className="w-5 h-5 text-primary" />
          Accounting Database
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Construction accounting workflow — organised by project phase.
        </p>
      </CardHeader>
      <CardContent>
        <Tabs value={active} onValueChange={setActive} className="space-y-4">
          <div className="overflow-x-auto -mx-1 px-1">
            <TabsList className="bg-muted/40 flex-wrap h-auto gap-1 justify-start w-full">
              {PHASES.map(p => {
                const Icon = p.icon;
                return (
                  <TabsTrigger
                    key={p.value}
                    value={p.value}
                    data-testid={`tab-${p.value}`}
                    className="whitespace-nowrap text-[11px] sm:text-xs gap-1.5"
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {p.short}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          {PHASES.map(p => (
            <TabsContent key={p.value} value={p.value} className="space-y-3">
              {p.value === 'phase-1' ? <P1SetupTable /> : <PhasePlaceholder phase={p} />}
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}

function PhasePlaceholder({ phase }) {
  const Icon = phase.icon;
  return (
    <Card className="border-border/40 bg-muted/10" data-testid={`phase-card-${phase.value}`}>
      <CardContent className="p-6 text-center space-y-3">
        <Icon className="w-10 h-10 mx-auto text-primary/70" />
        <div>
          <h3 className="text-base font-semibold text-foreground">{phase.label}</h3>
          <p className="text-xs text-muted-foreground mt-1">
            This section is ready for content. Let me know the fields, tables, or reports you need under this phase and I&apos;ll wire it up.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
